import { useCallback, useEffect, useRef } from 'react'
import { internalPrintId, type PrintShortcutHandler } from './ShortcutContext'
import { printLogger } from './utils'

export const usePrintShortcut = () => {
  const onPrintInvoked = useRef<PrintShortcutHandler>(undefined)

  const clearOnPrintInvoked = useCallback((context: string) => {
    if (onPrintInvoked.current && onPrintInvoked.current.context !== context) {
      printLogger.warn(
        `Cleared print context of '${onPrintInvoked.current.context}', while the caller wanted to clear '${context}', this is probably a user error.`,
      )
    }
    onPrintInvoked.current = undefined
  }, [])

  const setOnPrintInvoked = useCallback(
    (newHandler: PrintShortcutHandler) => {
      if (onPrintInvoked.current) {
        printLogger.warn(
          `Already attached a print context with context name '${onPrintInvoked.current.context}', replacing with '${newHandler?.context}'`,
        )
      }
      onPrintInvoked.current = newHandler
      return () => clearOnPrintInvoked(newHandler.context)
    },
    [clearOnPrintInvoked],
  )

  useEffect(() => {
    let isPrintHandlerDisabled = false
    const printInvoke = async (e: Event) => {
      if (isPrintHandlerDisabled) {
        printLogger.debug('Print shortcut disabled')
        return
      }
      printLogger.debug('Print Shortcut invoked')
      if (onPrintInvoked.current) {
        try {
          isPrintHandlerDisabled = true
          await onPrintInvoked.current.onPreparePrint(onPrintInvoked.current.context, e)
          window.print()
        } finally {
          isPrintHandlerDisabled = false
        }
      }
    }

    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        printLogger.debug('Print shortcut abort')
        if (onPrintInvoked.current && document.activeElement) {
          ;(document.activeElement as HTMLElement).blur()
        }
        return false
      }
      if (e.code === 'F3' || ((e.ctrlKey || e.metaKey) && e.code === 'KeyP') || e.key === 'PrintScreen' || e.key === 'Print') {
        printLogger.debug('Print Shortcut key')
        e.preventDefault()
        e.stopPropagation()
        void printInvoke(e)
        // indicate that the event was handled
        return true
      }
    }

    printLogger.debug('Print Handlers registered')

    const beforePrintHandler = (e: Event) => {
      printLogger.debug('Print Shortcut beforeprint', e, e.defaultPrevented)
      e.preventDefault()
      e.stopPropagation()
      void printInvoke(e)
      return true
    }

    window.addEventListener(internalPrintId, printInvoke)

    window.addEventListener('beforeprint', beforePrintHandler, {
      capture: true,
    })
    document.addEventListener('keydown', keyHandler)
    return () => {
      printLogger.debug('Print Handler removed')
      document.removeEventListener('keydown', keyHandler)
      window.removeEventListener(internalPrintId, printInvoke)
      window.removeEventListener('beforeprint', beforePrintHandler, {
        capture: true,
      })
    }
  }, [])

  return {
    handler: onPrintInvoked,
    /**
     * Use to register a new handler when the search shortcut is being invoked.
     * The handler returns a callback that can be used to unregister the handler.
     * @example
     * const { search: { setonPrintInvoked }} = useShortcutContext();
     * useEffect(() =>
     *  // automatically returns a callback that unregisters the shortcut when its lifetime has passed
     *  setonPrintInvoked({
     *    context: "handler-context-name",
     *    onSearch: () => { ... }
     *  }), []
     * )
     */
    setOnPrintInvoked,
    clearOnPrintInvoked,
  }
}
